import { useCoreGameStateInterest, useCoreGameStatesStore } from '@/services/coreGameStates';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { parseGameIdentity, type AdmittedGameState, type GameStateComparison, type SourceSummary } from '@/lib/gameState/admission';
import type { GameStateConnectionStatus } from '@/lib/gameState/transport';

const names: Record<string, string> = { kalshi: 'Kalshi', picktheodds: 'PickTheOdds',
  oddspapi: 'OddsPapi', 'mlb-statsapi': 'MLB StatsAPI', unabated: 'Unabated' };
function sourceName(source: string): string { return names[source] ?? source; }
export function sourceStatus(source: SourceSummary): string {
  if (source.finalReason === 'zombie') return 'Unconfirmed finish';
  if (source.statusId === 3) return source.finalReason === 'reported' ? 'Reported final' : 'Unconfirmed finish';
  if (source.statusId === 1) return 'Scheduled';
  return ['Reported live', source.periodLabel?.trim(), source.gameClock?.trim() || 'Clock unavailable'].filter(Boolean).join(' · ');
}
function score(source: SourceSummary): string {
  return `${source.awayScore ?? '—'} / ${source.homeScore ?? '—'}`;
}
function stamp(ms: number): string { return new Date(ms).toISOString().replace('T', ' ').replace('.000Z', 'Z').replace('Z', ' UTC'); }

export interface GameStateSourcesViewProps {
  row?: AdmittedGameState;
  comparison?: GameStateComparison;
  status: GameStateConnectionStatus;
  error: string | null;
  now: number;
}
export function GameStateSourcesView({ row, comparison, status, error, now }: GameStateSourcesViewProps) {
  const selected = row?.state;
  const connected = status === 'live';
  const invalid = row?.withdrawal === 'invalid-record';
  const description = !connected ? (status === 'connecting' || status === 'baseline' ? 'Connecting' : 'Disconnected · last known')
    : invalid ? 'Invalid score record' : !selected ? 'Score unavailable' : selected.stale ? 'Source marked stale'
      : selected.disputed ? 'Sources disagree' : 'Source comparison';
  const summaries = comparison?.sources ?? [];
  return (
    <section aria-label="Game state sources" className="border-border min-w-0 shrink-0 border-t px-3 py-2 text-xs">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        <h3 className="font-semibold">Game state sources</h3>
        <span className="text-muted-foreground">{description}</span>
      </div>
      {selected && (
        <div className="mt-1 min-w-0 break-words tabular-nums">
          <span className="font-medium">{sourceName(selected.source)}</span>: {selected.awayTeam} {selected.awayScore ?? '—'}
          {' · '}{selected.homeTeam} {selected.homeScore ?? '—'}
          <div className="text-muted-foreground">{sourceStatus(selected)}{selected.disputed && selected.stale ? ' · Sources disagree' : ''}</div>
          <time className="text-muted-foreground block" dateTime={new Date(selected.lastUpdateMs).toISOString()}>
            Observed {stamp(selected.lastUpdateMs)}
          </time>
        </div>
      )}
      {error && <p className="mt-1 text-amber-400">{error}</p>}
      {summaries.length > 0 ? (
        <>
          <div className="mt-2 max-h-60 overflow-auto" tabIndex={0} aria-label="Provider reports; scroll for more">
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-1 text-[11px] tabular-nums" aria-label="Provider reports">
              {summaries.map(source => <li key={source.source} className="border-border min-w-0 rounded border px-2 py-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 break-words font-medium">{sourceName(source.source)}</span>
                  <span className="shrink-0">{score(source)} <span className="text-muted-foreground">as reported</span></span>
                </div>
                <div className="mt-0.5 break-words">{sourceStatus(source)}</div>
                <time className="text-muted-foreground block break-words" dateTime={new Date(source.lastUpdateMs).toISOString()}>
                  Observed {stamp(source.lastUpdateMs)}
                </time>
              </li>)}
            </ul>
          </div>
          <p className="text-muted-foreground mt-1 text-[10px]">Scroll for more reports. Scores use each provider’s away / home order; team mapping is unverified.</p>
          <p className="text-muted-foreground mt-1 text-[10px]">Comparison received {comparison && stamp(comparison.receivedAtMs)}
            {comparison && now - comparison.receivedAtMs > 15_000 ? ' · Refresh overdue' : ''}</p>
        </>
      ) : <p className="text-muted-foreground mt-1">Provider reports unavailable.</p>}
      {!!comparison?.invalidSources && <p className="mt-1 text-amber-400">Some provider reports are invalid and unavailable.</p>}
    </section>
  );
}

export function GameStateSources({ gameKey }: { gameKey: string | null }) {
  useCoreGameStateInterest(gameKey);
  const configured = useCoreGameStatesStore(s => s.configured);
  const row = useCoreGameStatesStore(s => gameKey ? s.records.get(gameKey) : undefined);
  const comparison = useCoreGameStatesStore(s => gameKey ? s.comparisons.get(gameKey) : undefined);
  const status = useCoreGameStatesStore(s => s.status);
  const error = useCoreGameStatesStore(s => s.error);
  const now = useSharedNow(1000, configured);
  if (!configured || !gameKey || !parseGameIdentity(gameKey)) return null;
  return <GameStateSourcesView row={row} comparison={comparison} status={status} error={error} now={now} />;
}
